import { memo, useEffect, useState } from "react";
import MarketDataService from "../../services/market/MarketDataService";

const Sparkline = memo(({
  symbol,
  width = 80,
  height = 30,
  positiveColor = "#388ee3",
  negativeColor = "#eb434e"
}) => {

  const [priceHistory, setPriceHistory] = useState(() =>
    MarketDataService.getPriceHistory(symbol)
  );
  const [isPositive, setIsPositive] = useState(true);

  useEffect(() => {
    if (!symbol) return;

    const handlePriceUpdate = () => {
      const history = MarketDataService.getPriceHistory(symbol);
      setPriceHistory([...history]);

      if (history.length > 1) {
        setIsPositive(history[history.length - 1] >= history[0]);
      }
    };

    const unsubscribe = MarketDataService.subscribe(symbol, handlePriceUpdate);
    return unsubscribe;
  }, [symbol]);

  const getLinePath = () => {
    if (!priceHistory || priceHistory.length < 2) return "";

    const max = Math.max(...priceHistory);
    const min = Math.min(...priceHistory);
    const range = max - min || 1;
    const padding = 2;

    const points = priceHistory.map((value, index) => {
      const x = (index / (priceHistory.length - 1)) * width;
      const y =
        height -
        ((value - min) / range) * (height - padding * 2) -
        padding;

      return { x, y };
    });

    let d = `M ${points[0].x} ${points[0].y}`;
    points.forEach(p => {
      d += ` L ${p.x} ${p.y}`;
    });

    return d;
  };

  const getAreaPath = () => {
    if (!priceHistory || priceHistory.length < 2) {
      return generateDefaultAreaPath(width, height);
    }

    const max = Math.max(...priceHistory);
    const min = Math.min(...priceHistory);
    const range = max - min || 1;
    const padding = 2;

    const points = priceHistory.map((value, index) => {
      const x = (index / (priceHistory.length - 1)) * width;
      const y =
        height -
        ((value - min) / range) * (height - padding * 2) -
        padding;

      return { x, y };
    });

    let d = `M ${points[0].x} ${points[0].y}`;

    points.forEach(p => {
      d += ` L ${p.x} ${p.y}`;
    });

    d += ` L ${points[points.length - 1].x} ${height}`;
    d += ` L ${points[0].x} ${height} Z`;

    return d;
  };

  const generateDefaultAreaPath = (w, h) => {
    let d = `M 0 ${h / 2}`;
    const segments = 10;

    for (let i = 0; i <= segments; i++) {
      const x = (i / segments) * w;
      const y = h / 2 + Math.sin(i * 0.8) * (h / 4);
      d += ` L ${x} ${y}`;
    }

    d += ` L ${w} ${h} L 0 ${h} Z`;
    return d;
  };

  const color = isPositive ? positiveColor : negativeColor;

  return (
    <div
      style={{
        width,
        height,
        overflow: "hidden",
        borderRadius: 12
      }}
    >
      <svg width={width} height={height}>

        <defs>
          <linearGradient id={`grad-${symbol}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>

        <path
          d={getAreaPath()}
          fill={`url(#grad-${symbol})`}
          stroke="none"
        />

        <path
          d={getLinePath()}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

      </svg>
    </div>
  );
});

Sparkline.displayName = "Sparkline";

export default Sparkline;
